import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AceEditor from 'react-ace';
import ace from 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/mode-c_cpp';
import 'ace-builds/src-noconflict/mode-java';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-tomorrow_night';
import ChooseLanguage from './ChooseLanguage';

ace.config.set('basePath', 'ace-builds/src-noconflict');

function AddCode() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  let reactAceComponent;
  const [language, setLanguage] = useState('cpp');
  const [code, setCode] = useState(t('// paste your code here') + '\n');
  const [title, setTitle] = useState('');

  function onLanguageChange(languageId) {
    setLanguage(languageId[0]);
    if (reactAceComponent) {
      reactAceComponent.editor.getSession()
        .setMode('ace/mode/' + languageId[0]);
    }
  }

  function onCodeChange(newCode) { setCode(newCode); }
  function onTitleChange(e) { setTitle(e.target.value); }

  function submitCode() {
    const request = { language, source_code: code, title };
    fetch('/api/code/add', {
      method: 'POST',
      body: JSON.stringify(request),
    })
      .then(response => response.json())
      .then(data => {
        if (data.success === 1) {
          navigate('/code/' + data.code.id + '/view');
        }
      });
  }

  return (
    <div className="cs-add">
      {/* Main form */}
      <div className="cs-add__main">
        <h1>{t('Add code for review')}</h1>
        <div className="lede">
          {t('paste a snippet, describe the problem, and submit for review.')}
        </div>

        <div className="cs-field">
          <div className="cs-field__lbl">
            <span>{t('title')}</span>
            <span className="hint">{t('required')}</span>
          </div>
          <input
            id="problemName"
            type="text"
            placeholder={t('Write a title')}
            value={title}
            onChange={onTitleChange}
          />
        </div>

        <div className="cs-field">
          <div className="cs-field__lbl">
            <span>{t('language')}</span>
          </div>
          <ChooseLanguage onLanguageChange={onLanguageChange} />
        </div>

        <div className="cs-field">
          <div className="cs-field__lbl">
            <span>{t('code')}</span>
          </div>
          <div className="cs-code-input">
            <div className="cs-code-input__head">
              <BranchIcon />
              <span style={{ color: 'var(--fg-1)' }}>
                {t('untitled')}.{langExt(language)}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ color: 'var(--fg-3)', fontSize: 10.5 }}>
                {t('tab indents')}
              </span>
            </div>
            <AceEditor
              width="100%"
              ref={(c) => { reactAceComponent = c; }}
              mode={language === 'cpp' ? 'c_cpp' : language}
              theme="tomorrow_night"
              onChange={onCodeChange}
              name="UNIQUE_ID_OF_DIV"
              editorProps={{ $blockScrolling: true }}
              value={code}
              minLines={14}
              maxLines={32}
              setOptions={{ useWorker: false }}
              style={{ borderRadius: 0, background: 'var(--bg-2)' }}
            />
          </div>
        </div>

        <div className="cs-add__actions">
          <button
            className="cs-btn cs-btn--ghost"
            onClick={() => navigate('/code/feed')}
          >
            {t('Cancel')}
          </button>
          <div className="spacer" />
          <button
            id="submit-button"
            className="cs-btn cs-btn--primary"
            onClick={submitCode}
            disabled={!title.trim()}
          >
            <SendIcon /> {t('Submit')}
          </button>
        </div>
      </div>

      {/* Tips sidebar */}
      <aside className="cs-add__side">
        <h3>{t('Tips for a good submission')}</h3>

        {[
          [
            'Link the problem',
            'so reviewers can see the original exercise.',
          ],
          [
            'Describe what you tried',
            'especially edge cases you weren\'t sure about.',
          ],
          [
            'One concept per snippet',
            'break large solutions into parts.',
          ],
          [
            'Reply quickly',
            'reviewers return faster when you engage with comments.',
          ],
        ].map(([strong, rest], i) => (
          <div className="tip" key={i}>
            <div className="num">{i + 1}</div>
            <div>
              <strong>{t(strong)}</strong>
              {t(rest)}
            </div>
          </div>
        ))}
      </aside>
    </div>
  );
}

function langExt(lang) {
  const m = {
    cpp: 'cpp',
    c_cpp: 'cpp',
    java: 'java',
    python: 'py',
    javascript: 'js',
    sql: 'sql',
  };
  return m[lang] || 'txt';
}

function BranchIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ color: 'var(--fg-3)' }}
    >
      <path d="M6 3v12M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 15a9 9 0 0 0 9-9" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

export default AddCode;
